import { Alert, Card, Divider, Group, Select, Skeleton, Stack, Table, Text, TextInput } from "@mantine/core";
import { IconAlertTriangle, IconReceiptOff, IconSearch } from "@tabler/icons-react";
import { useState } from "react";
import useSWR from "swr";
import { TenantCollectedFeeApi } from "../../../apis";
import type { CollectedFee, CollectedFeeStatus } from "../../../apis/tenantCollectedFee";
import { PageLayout } from "../../../components/layout";
import { CollectedFeeStatusBadge } from "../../../components/billing/CollectedFeeStatusBadge";
import { EmptyState } from "../../../components/billing/EmptyState";
import { MoneyCell } from "../../../components/billing/MoneyCell";
import { MyCustomerName } from "../../../components/billing/MyCustomerName";
import { formatDate } from "../../../utils/format";

const footerBar = { background: "light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))" };

const breadcrumb = [
  { to: "/", label: "Эхлэл" },
  { to: "/billing", label: "Билл" },
  { label: "Цуглуулсан шимтгэл" }
];

const CollectedFeesList = () => {
  const [status, setStatus] = useState<CollectedFeeStatus | "">("");
  const [orderNumber, setOrderNumber] = useState("");

  const { data, error, isLoading } = useSWR(`swr.my.collected-fees.${status}.${orderNumber}`, () =>
    TenantCollectedFeeApi.list({
      status: status || undefined,
      orderNumber: orderNumber || undefined,
      page: 1, limit: 100
    })
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = data as any;
  const rows: CollectedFee[] = Array.isArray(raw) ? raw : (raw?.rows ?? []);
  const total = rows.reduce((sum, r) => sum + Number(r.finalFeeAmount || 0), 0);

  return (
    <PageLayout
      title="Цуглуулсан шимтгэл"
      subTitle="End-customer-аас цуглуулсан шимтгэлийн жагсаалт"
      breadcrumb={breadcrumb}>
      <Stack gap="md">
        <Group gap="sm">
          <Select
            placeholder="Бүх төлөв"
            data={[
              { value: "PENDING", label: "Хүлээгдэж буй" },
              { value: "DEDUCTED", label: "Хассан" },
              { value: "INVOICED", label: "Нэхэмжилсэн" },
              { value: "SETTLED", label: "Төлсөн" },
              { value: "WAIVED", label: "Хүчингүйд" },
              { value: "FAILED", label: "Алдсан" },
              { value: "REFUNDED", label: "Буцаасан" }
            ]}
            value={status || null}
            onChange={(v) => setStatus((v as CollectedFeeStatus) || "")}
            clearable w={200}
          />
          <TextInput
            placeholder="Захиалга №-ээр хайх"
            value={orderNumber}
            onChange={(e) => setOrderNumber(e.currentTarget.value)}
            leftSection={<IconSearch size={16} />}
            w={250}
          />
        </Group>

        {error ? (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>Шимтгэл ачааллахад алдаа гарлаа.</Alert>
        ) : isLoading ? (
          <Skeleton height={240} radius="md" />
        ) : rows.length === 0 ? (
          <Card withBorder radius="md" padding="md">
            <EmptyState icon={<IconReceiptOff size={26} />} message="Цуглуулсан шимтгэл алга байна." />
          </Card>
        ) : (
          <Card withBorder radius="md" padding={0}>
            <Table.ScrollContainer minWidth={640}>
              <Table verticalSpacing="sm" highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Огноо</Table.Th>
                    <Table.Th>Захиалга №</Table.Th>
                    <Table.Th>Харилцагч</Table.Th>
                    <Table.Th ta="right">Үндсэн дүн</Table.Th>
                    <Table.Th ta="right">Шимтгэл</Table.Th>
                    <Table.Th>Төлөв</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.map((r) => (
                    <Table.Tr key={r.id}>
                      <Table.Td style={{ fontVariantNumeric: "tabular-nums" }}>{formatDate(r.collectedAt)}</Table.Td>
                      <Table.Td style={{ fontVariantNumeric: "tabular-nums" }}>{r.orderNumber ?? r.orderId.substring(0, 8) + "…"}</Table.Td>
                      <Table.Td><MyCustomerName id={r.endCustomerBusinessId} /></Table.Td>
                      <Table.Td ta="right"><MoneyCell value={r.baseAmount} currency={r.baseCurrency} /></Table.Td>
                      <Table.Td ta="right"><MoneyCell value={r.finalFeeAmount} currency={r.feeCurrency} fw={500} /></Table.Td>
                      <Table.Td><CollectedFeeStatusBadge status={r.status} /></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
            <Divider />
            <Group justify="space-between" p="sm" style={footerBar}>
              <Text size="sm" c="dimmed">Нийт: {rows.length} бичлэг</Text>
              <Group gap={6} wrap="nowrap">
                <Text size="sm" c="dimmed">Бүгд:</Text>
                <MoneyCell value={total} currency={rows[0]?.feeCurrency} fw={700} c="brand.7" />
              </Group>
            </Group>
          </Card>
        )}
      </Stack>
    </PageLayout>
  );
};

export default CollectedFeesList;
